import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute } from '../components/routes';
import { RegistrationForm, LoginForm, PasswordResetForm } from '../components/auth';

// Lazy load pages for better performance
const Dashboard = React.lazy(() => import('../pages/Dashboard').then(module => ({ default: module.default })));
const Games = React.lazy(() => import('../pages/Games').then(module => ({ default: module.default })));
const Admin = React.lazy(() => import('../pages/Admin').then(module => ({ default: module.default })));

/**
 * AppRoutesContent component that contains the actual route definitions
 * This is separated from AppRoutes to allow for easier testing
 */
export const AppRoutesContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Authentication Routes - Only accessible when not authenticated */}
      {!isAuthenticated ? (
        <>
          {/* Login Route */}
          <Route
            path="/login"
            element={<LoginRoute />}
          />

          {/* Registration Route */}
          <Route
            path="/register"
            element={<RegistrationRoute />}
          />

          {/* Password Reset Route */}
          <Route
            path="/reset-password"
            element={<PasswordResetRoute />}
          />

          {/* Default redirect to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      ) : (
        <>
          {/* Protected Routes - Only accessible when authenticated */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingPage />}>
                  <Dashboard />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Games Route */}
          <Route
            path="/games"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingPage />}>
                  <Games />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Admin Route */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingPage />}>
                  <Admin />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Default redirect to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </>
      )}
    </Routes>
  );
};

/**
 * AppRoutes component that sets up all application routes
 * Handles authentication-based routing and redirects
 */
export const AppRoutes: React.FC = () => {
  return (
    <Router>
      <AppRoutesContent />
    </Router>
  );
};

/**
 * Login Route Component
 * Handles navigation between login, registration, and password reset
 */
const LoginRoute: React.FC = () => {
  const [currentForm, setCurrentForm] = React.useState<'login' | 'register' | 'reset'>('login');

  return (
    <>
      {currentForm === 'login' ? (
        <LoginForm
          onSuccess={() => {
            // Redirect will happen automatically via AppRoutes
            window.location.href = '/dashboard';
          }}
          onSwitchToRegister={() => setCurrentForm('register')}
          onForgotPassword={() => setCurrentForm('reset')}
        />
      ) : currentForm === 'register' ? (
        <RegistrationForm
          onSuccess={() => setCurrentForm('login')}
          onSwitchToLogin={() => setCurrentForm('login')}
        />
      ) : (
        <PasswordResetForm
          onSuccess={() => setCurrentForm('login')}
          onBackToLogin={() => setCurrentForm('login')}
        />
      )}
    </>
  );
};

/**
 * Registration Route Component
 * Standalone registration page
 */
const RegistrationRoute: React.FC = () => {
  return (
    <RegistrationForm
      onSuccess={() => {
        window.location.href = '/login';
      }}
      onSwitchToLogin={() => {
        window.location.href = '/login';
      }}
    />
  );
};

/**
 * Password Reset Route Component
 * Standalone password reset page
 */
const PasswordResetRoute: React.FC = () => {
  return (
    <PasswordResetForm
      onSuccess={() => {
        window.location.href = '/login';
      }}
      onBackToLogin={() => {
        window.location.href = '/login';
      }}
    />
  );
};

/**
 * Loading Page Component
 * Shown while lazy-loaded pages are loading
 */
const LoadingPage: React.FC = () => (
  <div className="app-loading">
    <div className="loading-spinner"></div>
    <p>Loading page...</p>
  </div>
);